import Papa from 'papaparse';
import type { PartDraft, PartType, PartCondition, PartStatus } from '@/types/Part';
import { CATEGORIES } from '@/data/categories';
import { PART_ICON_IDS, type PartIconId } from '@/icons/parts';

const CATEGORY_IDS = new Set<string>(CATEGORIES.map((c) => c.id).filter((id) => id !== 'all'));
const ICON_IDS = new Set<string>(PART_ICON_IDS);
const PART_TYPES = new Set<PartType>(['OEM', 'Reproduction']);
const PART_CONDITIONS = new Set<PartCondition>([
  'NOS',
  'Excellent',
  'Good',
  'Restored',
  'Restorable',
  'Core',
]);
const PART_STATUSES = new Set<PartStatus>(['live', 'reserved', 'sold', 'draft']);

export interface CsvRowResult {
  row: number;
  draft?: PartDraft;
  errors: string[];
}

export interface CsvParseResult {
  rows: CsvRowResult[];
  validCount: number;
  errorCount: number;
}

/**
 * Required columns: id, sku, title, year, category, iconId, type, condition, price, weight
 * Optional: casting, dateCode, fitment, description, verifiedBy, verifiedDate, authNotes, stock, status
 * fitment: pipe-separated, e.g. "1970 Cuda Hemi|1970 Challenger Hemi"
 */
export async function parsePartsCsv(file: File): Promise<CsvParseResult> {
  return new Promise((resolve, reject) => {
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => h.trim(),
      complete: (result) => {
        const rows: CsvRowResult[] = result.data.map((raw, idx) => validateRow(raw, idx + 2));
        const validCount = rows.filter((r) => r.errors.length === 0).length;
        resolve({
          rows,
          validCount,
          errorCount: rows.length - validCount,
        });
      },
      error: (err) => reject(err),
    });
  });
}

function validateRow(raw: Record<string, string>, rowNumber: number): CsvRowResult {
  const errors: string[] = [];
  const get = (key: string) => (raw[key] ?? '').trim();

  const id = get('id');
  const sku = get('sku');
  const title = get('title');
  const yearStr = get('year');
  const category = get('category');
  const iconId = get('iconId');
  const type = get('type');
  const condition = get('condition');
  const priceStr = get('price');
  const weightStr = get('weight');

  if (!id) errors.push('id is required');
  if (!sku) errors.push('sku is required');
  if (!title) errors.push('title is required');

  const year = parseInt(yearStr, 10);
  if (!yearStr || Number.isNaN(year) || year < 1900 || year > 2100) {
    errors.push(`year must be 1900-2100 (got "${yearStr}")`);
  }

  if (!CATEGORY_IDS.has(category)) {
    errors.push(`category "${category}" not in ${[...CATEGORY_IDS].join(', ')}`);
  }

  if (!ICON_IDS.has(iconId)) {
    errors.push(`iconId "${iconId}" not a known part icon`);
  }

  if (!PART_TYPES.has(type as PartType)) {
    errors.push(`type must be OEM or Reproduction (got "${type}")`);
  }

  if (!PART_CONDITIONS.has(condition as PartCondition)) {
    errors.push(`condition must be one of ${[...PART_CONDITIONS].join(', ')}`);
  }

  const price = parseFloat(priceStr);
  if (Number.isNaN(price) || price < 0) errors.push(`price must be a non-negative number`);
  const weight = parseFloat(weightStr);
  if (Number.isNaN(weight) || weight < 0) errors.push(`weight must be a non-negative number`);

  const statusRaw = get('status') || 'draft';
  if (!PART_STATUSES.has(statusRaw as PartStatus)) {
    errors.push(`status must be one of ${[...PART_STATUSES].join(', ')}`);
  }

  const stockStr = get('stock') || '0';
  const stock = parseInt(stockStr, 10);
  if (Number.isNaN(stock) || stock < 0) errors.push(`stock must be a non-negative integer`);

  if (errors.length > 0) return { row: rowNumber, errors };

  const fitmentRaw = get('fitment');
  const fitment = fitmentRaw
    ? fitmentRaw.split('|').map((s) => s.trim()).filter(Boolean)
    : [];

  const draft: PartDraft = {
    id,
    sku,
    title,
    year,
    category,
    iconId: iconId as PartIconId,
    type: type as PartType,
    condition: condition as PartCondition,
    price,
    weight,
    casting: get('casting') || undefined,
    dateCode: get('dateCode') || undefined,
    fitment,
    description: get('description'),
    photos: [],
    authentication: {
      verifiedBy: get('verifiedBy') || 'Unverified',
      verifiedDate: get('verifiedDate') || new Date().toISOString().slice(0, 10),
      notes: get('authNotes') || '',
    },
    stock,
    status: statusRaw as PartStatus,
  };

  return { row: rowNumber, draft, errors: [] };
}

/**
 * Generates a downloadable CSV template with all required + optional
 * columns and one example row drawn from the live sample-parts seed.
 */
export function partsCsvTemplate(): string {
  const headers = [
    'id', 'sku', 'title', 'year', 'category', 'iconId', 'type', 'condition',
    'price', 'weight', 'casting', 'dateCode', 'fitment', 'description',
    'verifiedBy', 'verifiedDate', 'authNotes', 'stock', 'status',
  ];
  const example = [
    'swc-example-001',
    'SWC-EXAMPLE-SKU',
    '1970 Example Hemi Part',
    '1970',
    'engine-block',
    'i-block',
    'OEM',
    'Excellent',
    '4500',
    '60',
    '2806178',
    '0G27',
    '1970 Cuda Hemi|1970 Challenger Hemi',
    'Description text goes here',
    'Kevin K.',
    '2026-05-14',
    'Authentication notes go here',
    '1',
    'live',
  ];
  return Papa.unparse([headers, example]);
}
